import { Breadcrumbs as MuiBreadcrumbs, Link, Typography } from '@mui/material';
import { useLocation, Link as RouterLink } from 'react-router-dom';

import { SETTINGS_SEGMENT_LABELS } from '../pages/settings/settings-sections.js';

import { useBreadcrumbLabel } from './breadcrumb-labels.js';

/** Human labels for known route segments; everything else is humanized. */
const LABELS: Record<string, string> = {
  // Settings groups and sections (DEV-132), named as in the settings menu.
  ...SETTINGS_SEGMENT_LABELS,
  dashboard: 'Dashboard',
  settings: 'System settings',
  integrations: 'Integrations',
  transactions: 'Transactions',
  customers: 'Customers',
  'aml-alerts': 'AML Alerts',
  'incoming-payments': 'Incoming Review',
  reconciliation: 'Reconciliation',
  reports: 'Reports',
  commissions: 'Fees & Commissions',
  system: 'System Settings',
  admin: 'Admin',
  users: 'Users',
  styleguide: 'Design System',
  actions: 'Actions',
  'access-denied': 'Access denied',
};

function looksLikeId(seg: string): boolean {
  return seg.length >= 16 || /^[0-9a-f]{8}-/i.test(seg);
}

function labelFor(seg: string, named: string | undefined): string {
  const known = LABELS[seg];
  if (known) return known;
  // An id shows the name its page reported; until that has loaded, a short id.
  if (looksLikeId(seg)) return named ?? `${seg.slice(0, 8)}…`;
  const s = seg.replace(/[-_]+/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function Crumb({ seg, path, isLast }: { seg: string; path: string; isLast: boolean }): JSX.Element {
  const label = labelFor(seg, useBreadcrumbLabel(seg));
  return isLast ? (
    <Typography sx={{ color: 'var(--text-accent)', fontWeight: 600 }}>{label}</Typography>
  ) : (
    <Link component={RouterLink} to={path} sx={{ color: 'text.secondary' }}>
      {label}
    </Link>
  );
}

export function Breadcrumbs(): JSX.Element {
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);

  return (
    <MuiBreadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
      <Link component={RouterLink} to="/dashboard" sx={{ color: 'text.secondary' }}>
        Home
      </Link>
      {segments.map((seg, idx) => {
        const path = '/' + segments.slice(0, idx + 1).join('/');
        return <Crumb key={path} seg={seg} path={path} isLast={idx === segments.length - 1} />;
      })}
    </MuiBreadcrumbs>
  );
}
